import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SavedSheet from '../SavedSheet';
import { bibleService } from '../../../services/bible';
import { fetchDailyVerse, resolveHomeBookName } from '../../../services/dailyVerse';
import BfHeader, { useBfHeaderState } from './BfHeader';
import BfHomeBackdrop from './BfHomeBackdrop';
import BfHomeSpot from './BfHomeSpot';
import BfChapterSelector from './BfChapterSelector';
import BfMenuSheet from './BfMenuSheet';
import { bfHaptic } from './BfHooks';
import { prefetchGuestVerseInsights } from '../../../services/guestVerseInsight';
import { t } from '../../../i18n/uiText';
import { getStoredLanguageOrDefault } from '../../../utils/bibleLocaleSync';

const VERSE_PREVIEW_MAX = 340;
const PSALMS_BOOK_ID = 19;

/** Цитата без висящих «:», «;», «,» и тире — стих обрывается на полуфразе. */
function trimVersePreview(text) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  const short = clean.length <= VERSE_PREVIEW_MAX ? clean : `${clean.slice(0, VERSE_PREVIEW_MAX - 1)}…`;
  return short.replace(/[\s,;:–—-]+$/u, '');
}

/**
 * BfHome — главная: стих дня с бэкенда, гостевой insight.
 */
export default function BfHome({
  translation = 'synodal',
  onOpenSearch,
}) {
  const navigate = useNavigate();
  const [books, setBooks] = useState([]);
  const [dailyVerse, setDailyVerse] = useState(null);
  const [dailyLoading, setDailyLoading] = useState(true);
  const [showChapterSelector, setShowChapterSelector] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const location = useLocation();
  // ?saved=1 — пришли из бота по кнопке «Открыть в приложении» под списком сохранённого
  const [showSaved, setShowSaved] = useState(() => new URLSearchParams(location.search).get('saved') === '1');
  const [insightMode, setInsightMode] = useState(null);
  const { hidden, scrolled } = useBfHeaderState();

  useEffect(() => {
    if (new URLSearchParams(location.search).get('saved') === '1') {
      navigate(location.pathname, { replace: true });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await bibleService.getBooks(translation);
        if (!cancelled) setBooks(list || []);
      } catch {
        /* no-op */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translation]);

  useEffect(() => {
    let cancelled = false;
    setDailyLoading(true);
    (async () => {
      try {
        const data = await fetchDailyVerse(translation, { refresh: true });
        if (!cancelled) setDailyVerse(data || null);
      } catch {
        if (!cancelled) setDailyVerse(null);
      } finally {
        if (!cancelled) setDailyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [translation]);

  const bookName = useMemo(() => {
    if (!dailyVerse?.bookId) return '';
    return (
      resolveHomeBookName(dailyVerse.bookId, books, dailyVerse.bookName) ||
      `Книга ${dailyVerse.bookId}`
    );
  }, [dailyVerse, books]);

  const spotTitle =
    parseInt(dailyVerse?.bookId, 10) === PSALMS_BOOK_ID ? t('psalmWord') : bookName;

  const verseNum = dailyVerse?.verse ? parseInt(dailyVerse.verse, 10) : 1;

  const versePreview = dailyVerse?.text ? trimVersePreview(dailyVerse.text) : '';

  const hasPassage = Boolean(
    dailyVerse?.bookId && dailyVerse?.chapter && (versePreview || dailyVerse?.text)
  );

  const verseDayKey = dailyVerse?.date ? `${dailyVerse.date}:${translation}` : null;

  useEffect(() => {
    if (!dailyVerse?.bookId || !dailyVerse?.chapter || !dailyVerse?.verse) return;
    prefetchGuestVerseInsights({
      bookId: parseInt(dailyVerse.bookId, 10),
      chapter: parseInt(dailyVerse.chapter, 10),
      verse: parseInt(dailyVerse.verse, 10) || 1,
      translation,
      modes: ['simple'],
    });
  }, [dailyVerse?.bookId, dailyVerse?.chapter, dailyVerse?.verse, translation]);

  const navigateToBookmarks = () => {
    setShowMenu(false);
    setShowSaved(true);
  };

  const handleOpenPassage = () => {
    bfHaptic('light');
    if (hasPassage && dailyVerse) {
      const v = dailyVerse.verse ? `/${dailyVerse.verse}` : '';
      navigate(`/${translation}/${dailyVerse.bookId}/${dailyVerse.chapter}${v}`);
      return;
    }
    setShowChapterSelector(true);
  };

  const handleInsight = (mode) => {
    if (!hasPassage || !dailyVerse?.bookId) return;
    bfHaptic('light');
    setInsightMode(mode);
  };

  const uiLanguage = getStoredLanguageOrDefault();

  const dateLabel = useMemo(() => {
    try {
      return new Date().toLocaleDateString(uiLanguage, { day: 'numeric', month: 'long' });
    } catch {
      return '';
    }
  }, [uiLanguage]);

  return (
    <div className="bf-app bf-app--home">
      <BfHomeBackdrop />

      <BfHeader
        bookName={t('bible')}
        chapter=""
        hidden={hidden}
        scrolled={scrolled}
        onOpenChapterSelector={() => setShowChapterSelector(true)}
        onOpenSearch={onOpenSearch}
        onOpenMenu={() => setShowMenu(true)}
      />

      <main className="bf-home bf-home--compact">
        <BfHomeSpot
          dateLabel={dateLabel}
          language={uiLanguage}
          bookName={spotTitle}
          chapter={dailyVerse?.chapter}
          verse={dailyVerse?.verse ?? verseNum}
          versePreview={versePreview}
          bookId={dailyVerse?.bookId ? parseInt(dailyVerse.bookId, 10) : null}
          translation={translation}
          loading={dailyLoading}
          hasPassage={hasPassage}
          insightMode={insightMode}
          onInsightModeChange={setInsightMode}
          onOpenPassage={handleOpenPassage}
          onOpenInsight={handleInsight}
        />
      </main>

      {showChapterSelector && (
        <BfChapterSelector
          bookId={dailyVerse?.bookId || 1}
          currentChapter={dailyVerse?.chapter || 1}
          totalChapters={50}
          bookName={bookName || ''}
          books={books}
          onSelect={(bId, chap) => {
            setShowChapterSelector(false);
            navigate(`/${translation}/${bId}/${chap}`);
          }}
          onClose={() => setShowChapterSelector(false)}
        />
      )}

      {showMenu && (
        <BfMenuSheet
          onClose={() => setShowMenu(false)}
          onSelectChapter={() => {
            setShowMenu(false);
            setShowChapterSelector(true);
          }}
          onOpenSearch={
            onOpenSearch
              ? () => {
                  setShowMenu(false);
                  onOpenSearch();
                }
              : undefined
          }
          onShowBookmarks={navigateToBookmarks}
          onGoSettings={() => {
            setShowMenu(false);
            navigate('/settings');
          }}
        />
      )}

      {showSaved && <SavedSheet translation={translation} onClose={() => setShowSaved(false)} />}
    </div>
  );
}
